import {
  laboratories,
  LAB_SAMPLE_STATUSES,
  LAB_SAMPLE_STATUSES_DESC,
  ORDER_LOCK_TYPE,
  SAMPLE_REPORT_STATUSES,
  SAMPLE_STATUSES,
  SampleReportType,
} from '@/config/constants';

const analysesStatusColumns = {
  stabilityToken: {
    key: 'stabilityTimepointToken',
    label: 'stabilityToken',
    type: 'text',
    sortable: 'custom',
    minWidth: 100,
  },
  stabilityTemperatureHumidity: {
    key: 'stabilityTemperature',
    label: 'stabilityTemperatureHumidity',
    type: 'function',
    sortable: false,
    minWidth: 130,
    get: (value, row) => {
      if (value == null) return '';
      const humidityPart =
        row.stabilityHumidity != null ? ` ${row.stabilityHumidity}%RH` : '';
      return `${value}°C${humidityPart}`;
    },
  },
  stabilityMatrix: {
    key: 'stabilityMatrix',
    label: 'stabilityMatrix',
    type: 'text',
    sortable: false,
    minWidth: 120,
  },
  groupingId: {
    key: 'groupingId',
    label: 'grouping',
    type: 'text',
    sortable: 'custom',
    minWidth: 150,
  },
  sampleId: {
    key: 'sampleId',
    label: 'customerSampleId',
    type: 'function',
    sortable: 'custom',
    minWidth: 150,
    get: (value, row) => (row.children ? '' : value),
  },
  id: {
    key: 'labSampleCode',
    label: 'sampleId',
    type: 'function',
    sortable: 'custom',
    minWidth: 150,
    get: (value, row) => (row.children ? '' : value),
  },
  customerProductName: {
    key: 'customerProductName',
    label: 'customerSampleCode',
    type: 'text',
    sortable: 'custom',
    minWidth: 150,
  },
  finalCustomer: {
    key: 'finalCustomer',
    label: 'finalCustomer',
    type: 'text',
    sortable: 'custom',
    minWidth: 200,
  },
  finalCustomerRef: {
    key: 'finalCustomerRef',
    label: 'finalCustomerRef',
    type: 'text',
    sortable: false,
    minWidth: 130,
  },
  status: {
    key: 'status',
    label: 'status',
    type: 'encoded',
    sortable: false,
    customClass: true,
    enum: 'sampleStatuses',
    decode: (value) => LAB_SAMPLE_STATUSES_DESC[value],
    getClassModifier: (value) => LAB_SAMPLE_STATUSES_DESC[value],
    minWidth: 130,
  },
  productCode: {
    key: 'productCode',
    label: 'productCode',
    type: 'text',
    sortable: 'custom',
    minWidth: 150,
  },
  productName: {
    key: 'productName',
    label: 'productName',
    type: 'text',
    sortable: 'custom',
    minWidth: 150,
  },
  lot: {
    key: 'lot',
    label: 'lot',
    type: 'text',
    sortable: 'custom',
    minWidth: 130,
  },
  lotDate: {
    key: 'date',
    label: 'lotDate',
    type: 'date',
    sortable: false,
    minWidth: 130,
  },
  dilution: {
    key: 'dilution',
    label: 'dilution',
    type: 'enum',
    enum: 'dilutions',
    minWidth: 85,
    sortable: false,
  },
  date: {
    key: 'acceptedAt',
    label: 'receivedDate',
    type: 'date',
    sortable: 'custom',
    minWidth: 170,
  },
  quarantineDate: {
    key: 'quarantineDate',
    label: 'quarantineDate',
    type: 'date',
    sortable: 'custom',
    minWidth: 170,
  },
  startDate: {
    key: 'sampleDates.sampleStart',
    label: 'startDate',
    type: 'date',
    sortable: false,
    minWidth: 130,
  },
  endDate: {
    key: 'sampleDates.sampleEnd',
    label: 'endDate',
    type: 'date',
    sortable: false,
    minWidth: 130,
  },
  expectedDeliveryDate: {
    key: 'expectedDeliveryDate',
    label: 'expectedDeliveryDate',
    type: 'date',
    sortable: false,
    minWidth: 130,
  },
  notes: {
    key: 'notes',
    label: 'customerNotes',
    type: 'text',
    sortable: false,
    minWidth: 200,
  },
  sampleDescription: {
    key: 'sampleDescription',
    label: 'sampleDescription',
    type: 'enum',
    enum: 'sampleDescriptions',
    sortable: 'custom',
    minWidth: 200,
  },
  sector: {
    key: 'sector',
    label: 'sector',
    type: 'enum',
    enum: 'sectors',
    sortable: 'custom',
    minWidth: 230,
  },
  orderId: {
    key: 'orderId',
    label: 'orderId',
    type: 'text',
    sortable: 'custom',
    minWidth: 130,
  },
  acceptedByUsername: {
    key: 'acceptedByUsername',
    label: 'approvalUser',
    type: 'text',
    sortable: false,
    minWidth: 130,
  },
  actionReason: {
    key: 'actionReason',
    label: 'actionReason',
    type: 'text',
    sortable: false,
    minWidth: 130,
  },
  actions: {
    key: 'actions',
    label: 'actions',
    type: 'actions',
    sortable: false,
    fixed: true,
    minWidth: 230,
    buttons: [
      {
        name: 'proceedLocked',
        icon: 'fas el-icon-warning-outline',
        showCondition: (row) =>
          row.orderLocked === ORDER_LOCK_TYPE.ACCEPTANCE ||
          row.orderLocked === ORDER_LOCK_TYPE.ACCEPTANCE_REPORT,
      },
      {
        name: 'reportLocked',
        icon: 'fas el-icon-warning',
        showCondition: (row) => row.orderLocked === ORDER_LOCK_TYPE.REPORT,
      },
      {
        name: 'viewAnalysesDetails',
        icon: 'fas fa-eye',
        permissions: ['analysesStatusView'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE_REPORT,
      },
      {
        name: 'showComment',
        icon: 'fas fa-comment',
        showCondition: (row) => row.comments,
      },
      {
        name: 'changeSampleStatus',
        icon: 'fas fa-retweet',
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE_REPORT &&
          !(row.status === LAB_SAMPLE_STATUSES.CANCELED),
      },
      {
        name: (row) => {
          if (row.status === LAB_SAMPLE_STATUSES.COMPLETED) {
            return 'viewLabNotes';
          }
          return row.labNotes ? 'editLabNotes' : 'createLabNotes';
        },
        icon: 'fas fa-file-pen',
        secondaryIcon: (row) => {
          if (row.status === LAB_SAMPLE_STATUSES.COMPLETED) {
            return 'fas fa-file-circle-minus';
          }
          return row.labNotes ? 'fas fa-file-lines' : 'fas fa-file-pen';
        },
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE_REPORT &&
          !(row.status === LAB_SAMPLE_STATUSES.CANCELED),
      },
      {
        name: 'changeDilution',
        icon: 'fas fa-flask',
        permissions: ['analysesDataManage'],
        showCondition: (row) =>
          row.laboratory === laboratories.MICR &&
          !(
            row.status === LAB_SAMPLE_STATUSES.CANCELED ||
            row.status === LAB_SAMPLE_STATUSES.COMPLETED
          ),
      },
      {
        name: 'editSamplingInformation',
        icon: 'fas fa-tag',
        permissions: ['analysesDataManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE_REPORT &&
          !(
            row.status === LAB_SAMPLE_STATUSES.CANCELED ||
            row.status === LAB_SAMPLE_STATUSES.COMPLETED
          ),
      },
      {
        name: 'printSample',
        icon: 'fas fa-print',
        permissions: ['printReportManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.REPORT &&
          row.status === LAB_SAMPLE_STATUSES.COMPLETED,
      },
      {
        name: 'viewReports',
        icon: 'fas fa-file-chart-line',
        secondaryIcon: (row) =>
          `${
            row.sampleReportStatus === null ||
            row.sampleReportStatus === 'inProgress'
              ? 'fad fa-file-minus'
              : 'fas fa-file-chart-line'
          }`,
        permissions: ['analysesStatusView'],
        disableCondition: (row) =>
          row.sampleReportStatus === null ||
          row.sampleReportStatus === 'inProgress',
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.REPORT &&
          row.status === LAB_SAMPLE_STATUSES.COMPLETED,
      },
      {
        name: 'sampleDraft',
        icon: 'fas fa-file-edit',
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.REPORT &&
          row.sampleReportStatus === SAMPLE_REPORT_STATUSES.TOBEAPPROVED &&
          row.status !== LAB_SAMPLE_STATUSES.CANCELED,
      },
      {
        name: 'removeSampleDraft',
        icon: 'fas fa-check-square',
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.REPORT &&
          row.sampleReportStatus === SAMPLE_REPORT_STATUSES.DRAFT,
      },
      {
        name: 'renameSample',
        icon: 'fas fa-edit',
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.orderLocked !== ORDER_LOCK_TYPE.REPORT &&
          row.sampleReportStatus === SAMPLE_REPORT_STATUSES.DRAFT,
      },
      {
        name: 'createSampleRevision',
        icon: 'fas fa-glasses',
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.orderLocked !== ORDER_LOCK_TYPE.ACCEPTANCE &&
          row.status === LAB_SAMPLE_STATUSES.COMPLETED &&
          row.sampleReportStatus === SAMPLE_REPORT_STATUSES.APPROVED,
      },
      {
        name: 'uploadDocument',
        icon: 'fas fa-file-upload',
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.sampleReportStatus === SAMPLE_REPORT_STATUSES.DOCUMENTPENDING,
      },
      {
        name: 'removeUploadedReport',
        icon: 'fas fa-trash-alt',
        permissions: ['analysesStatusManage'],
        showCondition: (row) =>
          row.sampleReportType === SampleReportType.Challenge &&
          row.sampleReportStatus === SAMPLE_REPORT_STATUSES.TOBEAPPROVED &&
          // A signed report concludes the sample, and a revision upload does not reopen it
          row.sampleStatus !== SAMPLE_STATUSES.CONCLUDED,
      },
      {
        name: 'downloadAttachment',
        icon: 'fas fa-paperclip',
        showCondition: (row) => row.hasAttachments,
        permissions: ['analysesStatusManage'],
      },
    ],
  },
};

export default analysesStatusColumns;
